import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import { useAppDispatch } from 'reducers/index';

// Types
import type { UpdateCmsPageRequest } from '@zola/svc-web-api-ts-client';
import type { EditPageV2Props } from 'components/manage/website/entities/EditPageCommon/EditPageFormContainer/EditPageFormV2/index';
import type { ZolaFileResponseType } from 'actions/types/UploadcareActions';
import { UploadCareTabTypes } from '@zola-helpers/client/dist/es/uploadcare/constants';

// action
import { uploaded } from 'actions/website/FileUploadActions';

// React-Final-Form
import { Form, FormSpy, Field } from 'react-final-form';
import { FormHiddenInput } from 'components/common/form/FinalFormV2Elements';
import FormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import TextareaFieldAdapterV2 from '@zola/zola-ui/src/components/Form/TextareaFieldV2/TextareaFieldV2Adapter';
import ImageUploadV2 from 'components/common/ImageUploadV2/ImageUploadV2';
import * as Validations from 'components/common/form/commonValidations';

// Content
import { getCmsPageDescriptionPlaceholder } from '../../../../CmsPageTypes';

// Styles
// import '../EditPageForm.less';
import styles from './EditPageFormV2.module.less';
import sharedStyles from '../../editPageSharedStyles.module.less';

const EditPageFormV2: React.FC<EditPageV2Props> = ({
  page,
  router,
  routes,
  submitPageSilently,
  submitPage,
  updatePreview,
}) => {
  const dispatch = useAppDispatch();

  const pageType = page && page.type;
  const descriptionPlaceholder = getCmsPageDescriptionPlaceholder(pageType);

  useEffect(() => {
    return () => {
      router.setRouteLeaveHook(routes[routes.length - 1], () => {
        // do nothing
      });
    };
  });

  function onUploadCompleted(zolaFile: ZolaFileResponseType) {
    dispatch(
      uploaded(
        {
          ...zolaFile,
        },
        `header_image_${pageType}`
      )
    );
  }

  return (
    <Form
      onSubmit={async (
        values: Omit<UpdateCmsPageRequest, 'wedding_account_id'> & { header_image_url?: string }
      ) => {
        await submitPage(values);
      }}
      initialValues={page}
    >
      {({ handleSubmit, form: { change, getState } }) => (
        <form onSubmit={handleSubmit} id="edit-page-form" noValidate>
          <FormHiddenInput id="cms-page-id" name="id" />
          <FormHiddenInput id="cms-page-hidden" name="hidden" />
          <FormHiddenInput name="header_image_id" />
          <FormHiddenInput name="header_image_url" />
          <FormInput
            name="title"
            type="text"
            label="Page Title"
            id="cms-page-title"
            required
            maxChars={40}
            validate={[Validations.REQUIRED, Validations.MAX_LENGTH_40]}
            onBlur={async () => {
              const { dirty, valid } = getState();
              if (dirty && valid) {
                await handleSubmit();
              }
            }}
            containerClassname={styles.titleField}
          />
          <div className={sharedStyles.imageUploadSpacing}>
            <label className={sharedStyles.label}>
              Add a Photo <span className={sharedStyles.optionalLabel}>(Optional)</span>
            </label>
            <ImageUploadV2
              imageUrl={getState().values.header_image_url}
              onUploadComplete={async zolaFile => {
                change('header_image_id', zolaFile.uuid);
                change('header_image_url', zolaFile.url);
                await submitPageSilently(getState().values).then(() => {
                  onUploadCompleted(zolaFile);
                });
              }}
              onClearImage={async () => {
                change('header_image_id', undefined);
                change('header_image_url', undefined);
                await submitPageSilently(getState().values);
              }}
              effects="mirror,enhance,sharp,blur,grayscale,crop"
              tabs={[
                UploadCareTabTypes.FILE,
                UploadCareTabTypes.DROPBOX,
                UploadCareTabTypes.GOOGLE_PHOTOS,
                UploadCareTabTypes.ONE_DRIVE,
                UploadCareTabTypes.FACEBOOK,
                UploadCareTabTypes.INSTAGRAM,
              ]}
              analyticsLocation="WEBSITE"
              analyticsProduct="WEBSITE"
              maxSize={5}
            />
          </div>
          <Field
            name="description"
            id="cms-page-description"
            label="Description"
            component={TextareaFieldAdapterV2}
            placeholder={descriptionPlaceholder}
            maxChars={1000}
            rows={4}
            validate={Validations.MAX_LENGTH_1000}
            onBlurCB={async () => {
              const { dirty, valid } = getState();
              if (dirty && valid) {
                await handleSubmit();
              }
            }}
            // className="page-description"
          />
          <FormSpy
            subscription={{ values: true }}
            onChange={({ values }) => {
              let { title, description, hidden, header_image_url } = values;
              title = title || '';
              description = description || '';
              hidden = hidden || false;
              header_image_url = header_image_url || '';
              updatePreview({ title, description, hidden, header_image_url });
            }}
          />
        </form>
      )}
    </Form>
  );
};

export const EditPageFormV2WithRouter = withRouter(EditPageFormV2);
